import React from 'react';
import InvoiceLineItems from '../components/InvoiceLineItems';
import InlineEdit from '../components/InlineEdit';
import InvoiceSettings from '../components/InvoiceSettings';
import { useInvoiceStore } from '../stores/invoiceStore';
import { CommandBar } from '../components/CommandBar';
import { InvoiceOverview } from '../components/InvoiceOverview';
import { useTranslation } from '../hooks/useTranslation';
import InvoiceNumberEdit from '../components/InvoiceNumberEdit';
import { FloatingLabelInput } from '../components/shared/FloatingLabelInput';
import InvoiceDateEdit from '../components/InvoiceDateEdit';
import { CompanyAutosuggest } from '../components/shared/CompanyAutosuggest';
import { companyApi } from '../lib/api';
import { Company, CompanyData } from '@dutchinvoice/types';
import { Easy } from '../components/landing/easySection';
import PaymentMethodEdit from '../components/PaymentMethodEdit';
import ScheduleEdit from '../components/ScheduleEdit';
import { NewInvoiceButton } from '../components/NewInvoiceButton';
import { Hero } from '../components/landing/Hero';
import { SimpleFaqsWithBackground } from '../components/landing/Faq';
import { Footer } from '../components/landing/Footer';
import { Head } from '../components/Head';
import { ClientOnly } from '../components/ClientOnly';
import { ClientOnlyLoginButton } from '../components/Auth/LoginButton';
import LogoUploader from '../components/LogoUploader';
import { motion, AnimatePresence } from 'framer-motion';
import { InvoiceNonEditableOverlay } from '../components/InvoiceNonEditableOverlay';
import { AppleCardsCarouselDemo } from '../components/landing/AppleCardsCarouselDemo';


const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const currentInvoice = useInvoiceStore((state) => state.currentInvoice);
  const editingSection = useInvoiceStore((state) => state.editingSection);
  const isCurrentCompanySaved = useInvoiceStore((state) => state.isCurrentCompanySaved());
  const { updateSender, updateRecipient, updateNotes, setEditingSection, settings, saveAsDefaultCompany, setCompanyFromDatabase, setCompanyId } = useInvoiceStore();

  // Helper to get input value - empty if placeholder, actual value if not
  const getInputValue = (section: 'sender' | 'recipient', field: string): string => {
    const isPlaceholder = section === 'sender'
      ? currentInvoice.sender.isPlaceholder?.[field as keyof typeof currentInvoice.sender.isPlaceholder]
      : currentInvoice.recipient.isPlaceholder?.[field as keyof typeof currentInvoice.recipient.isPlaceholder];

    const value = section === 'sender'
      ? currentInvoice.sender[field as keyof typeof currentInvoice.sender]
      : currentInvoice.recipient[field as keyof typeof currentInvoice.recipient];

    // Ensure we always return a string
    return isPlaceholder ? '' : (typeof value === 'string' ? value : '');
  };

  // Helper to get placeholder - empty if focused
  const getPlaceholder = (value: string, isFocused: boolean) => {
    return isFocused ? '' : value;
  };

  const [focusedFields, setFocusedFields] = React.useState<{
    sender: { [key: string]: boolean };
    recipient: { [key: string]: boolean };
    notes: boolean;
  }>({
    sender: {},
    recipient: {},
    notes: false
  });

  const handleFocus = (section: 'sender' | 'recipient' | 'notes', field?: string) => {
    setFocusedFields(prev => ({
      ...prev,
      [section]: field
        ? { ...prev[section as 'sender' | 'recipient'], [field]: true }
        : true
    }));
  };

  const handleBlur = (section: 'sender' | 'recipient' | 'notes', field?: string) => {
    setFocusedFields(prev => ({
      ...prev,
      [section]: field
        ? { ...prev[section as 'sender' | 'recipient'], [field]: false }
        : false
    }));
  };

  // Handle company selection from autosuggest
  const handleCompanySelect = (company: Company) => {
    setCompanyFromDatabase(company);
  };

  // Handle clearing the company selection
  const handleClearCompany = () => {
    // Clear the companyId
    setCompanyId(undefined);

    // Also clear the company name
    updateRecipient('company', '');
  };

  // Handle company creation
  const handleCreateCompany = async (name: string): Promise<Company> => {
    try {
      // Create a new company with the current recipient data
      const companyData: CompanyData = {
        name,
        address: getInputValue('recipient', 'address'),
        postalCode: getInputValue('recipient', 'postalCode'),
        city: getInputValue('recipient', 'city'),
        contactName: getInputValue('recipient', 'contactName'),
        email: getInputValue('recipient', 'email'),
        additionalInfo: getInputValue('recipient', 'additionalInfo')
      };

      const newCompany = await companyApi.create(companyData);

      // Update the invoice with the new company
      setCompanyFromDatabase(newCompany);

      return newCompany;
    } catch (error) {
      console.error('Failed to create company:', error);
      throw error;
    }
  };

  const renderInvoiceForm = () => {
   return (   
      <div className="bg-white rounded-2xl p-4 xs:p-6 sm:p-8 invoice-content relative" data-html2canvas-ignore-absolute="true">
        {/* Header with smooth logo transition */}
        <motion.div 
          className="border-b pb-4 mb-4 xs:pb-6 xs:mb-6 invoice-header"
          layout 
          transition={{ 
            type: "spring", 
            stiffness: 200, 
            damping: 30, 
            duration: 0.3 
          }}
        >
          {/* Logo in its own row when present */}
          <AnimatePresence mode="popLayout">
            {settings.logo?.url && (
              <motion.div 
                initial={{ opacity: 0, y: -20, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -10, height: 0 }}
                transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                className="flex justify-center mb-[-18px] mt-[-18px] overflow-hidden"
                key="logo-standalone"
              >
                <div className="w-32 xs:w-40 sm:w-48 min-w-32 mb-8 mt-2 xl:mb-0 xs:mt-0 xs:min-w-40 sm:min-w-48">
                  <LogoUploader compact />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <div className="flex flex-col space-y-4 md:flex-row md:justify-between md:items-start md:space-y-0">
            <ClientOnly>
              <InvoiceNumberEdit
                isEditing={editingSection?.type === 'invoiceNumber'}
                onEditClick={() => setEditingSection({ type: 'invoiceNumber' })}
                onSave={() => setEditingSection(null)}
              />
            </ClientOnly>
            
            {/* Logo uploader centered in header only when no logo */}
            <AnimatePresence mode="popLayout">
              {!settings.logo?.url && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
                  className="flex-1 flex justify-center mt-2 order-last md:order-none"
                  key="logo-inline"
                >
                  <div className="w-32 xs:w-40 sm:w-48 min-w-32 xs:min-w-40 sm:min-w-48">
                    <LogoUploader compact />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            <div className="flex items-center space-x-4">
              <ClientOnly>
                <InvoiceDateEdit
                  isEditing={editingSection?.type === 'paymentTerms'}
                  onEditClick={() => setEditingSection({ type: 'paymentTerms' })}
                  onSave={() => setEditingSection(null)}
                />
              </ClientOnly>
            </div>
          </div>
        </motion.div>

        {/* Sender & Recipient Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 xs:gap-6 md:gap-8 mb-6 xs:mb-8 invoice-sender-receiver">
          <ClientOnly>
            <InlineEdit
              isEditing={editingSection?.type === 'recipient'}
              onEditClick={() => setEditingSection({ type: 'recipient' })}
              onSave={() => setEditingSection(null)}
              isPlaceholder={currentInvoice.recipient.isPlaceholder}
              displayContent={
                <div>
                  <h3 className="text-sm font-medium tracking-wide text-gray-500 uppercase mb-4">{t('invoice.to')}</h3>
                  <div className="space-y-3">
                    <div>
                      <p className={currentInvoice.recipient.isPlaceholder?.company ? "text-gray-400 italic text-lg" : "text-xl font-medium text-gray-900"}>{currentInvoice.recipient.company}</p>
                      <div className="mt-2 space-y-1 text-gray-600">
                        <p className={currentInvoice.recipient.isPlaceholder?.address ? "text-gray-400 italic" : ""}>{currentInvoice.recipient.address}</p>
                        <p className={currentInvoice.recipient.isPlaceholder?.postalCode || currentInvoice.recipient.isPlaceholder?.city ? "text-gray-400 italic" : ""}>
                          {currentInvoice.recipient.postalCode} {currentInvoice.recipient.city}
                        </p>
                        {currentInvoice.recipient.additionalInfo && (
                          <p className={currentInvoice.recipient.isPlaceholder?.additionalInfo ? "text-gray-400 italic" : "text-gray-600"}>
                            {currentInvoice.recipient.additionalInfo}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              }
              editContent={
                <div className="space-y-4">
                  <CompanyAutosuggest
                    value={getInputValue('recipient', 'company')}
                    onChange={(value) => {
                      // Update the company name in the invoice
                      updateRecipient('company', value);

                      // If the company name is cleared, also clear the companyId
                      if (!value.trim() && currentInvoice.companyId) {
                        handleClearCompany();
                      }
                    }}
                    onCompanySelect={handleCompanySelect}
                    onCreateCompany={handleCreateCompany}
                    onClearCompany={handleClearCompany}
                    companyId={currentInvoice.companyId}
                    placeholder={getPlaceholder(currentInvoice.recipient.company, focusedFields.recipient.company || false)}
                    label={t('invoice.company')}
                    isPlaceholder={currentInvoice.recipient.isPlaceholder?.company}
                    autoFocus={true}
                    onCustomFocus={() => handleFocus('recipient', 'company')}
                    onCustomBlur={() => handleBlur('recipient', 'company')}
                  />
                  <FloatingLabelInput
                    name="address"
                    type="text"
                    value={getInputValue('recipient', 'address')}
                    placeholder={getPlaceholder(currentInvoice.recipient.address, focusedFields.recipient.address || false)}
                    onChange={(e) => updateRecipient('address', e.target.value)}
                    onCustomFocus={() => handleFocus('recipient', 'address')}
                    onCustomBlur={() => handleBlur('recipient', 'address')}
                    label={t('invoice.address')}
                    isPlaceholder={currentInvoice.recipient.isPlaceholder?.address}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <FloatingLabelInput
                      name="postalCode"
                      type="text"
                      value={getInputValue('recipient', 'postalCode')}
                      placeholder={getPlaceholder(currentInvoice.recipient.postalCode, focusedFields.recipient.postalCode || false)}
                      onChange={(e) => updateRecipient('postalCode', e.target.value)}
                      onCustomFocus={() => handleFocus('recipient', 'postalCode')}
                      onCustomBlur={() => handleBlur('recipient', 'postalCode')}
                      label={t('invoice.postalCode')}
                      isPlaceholder={currentInvoice.recipient.isPlaceholder?.postalCode}
                    />
                    <FloatingLabelInput
                      name="city"
                      type="text"
                      value={getInputValue('recipient', 'city')}
                      placeholder={getPlaceholder(currentInvoice.recipient.city, focusedFields.recipient.city || false)}
                      onChange={(e) => updateRecipient('city', e.target.value)}
                      onCustomFocus={() => handleFocus('recipient', 'city')}
                      onCustomBlur={() => handleBlur('recipient', 'city')}
                      label={t('invoice.city')}
                      isPlaceholder={currentInvoice.recipient.isPlaceholder?.city}
                    />
                  </div>
                  <FloatingLabelInput
                    name="additionalInfo"
                    type="text"
                    value={getInputValue('recipient', 'additionalInfo')}
                    placeholder={getPlaceholder(currentInvoice.recipient.additionalInfo || '', focusedFields.recipient.additionalInfo || false)}
                    onChange={(e) => updateRecipient('additionalInfo', e.target.value)}
                    onCustomFocus={() => handleFocus('recipient', 'additionalInfo')}
                    onCustomBlur={() => handleBlur('recipient', 'additionalInfo')}
                    label={t('invoice.additionalInfo')}
                    isPlaceholder={currentInvoice.recipient.isPlaceholder?.additionalInfo}
                  />
                </div>
              }
            />
          </ClientOnly>

          <ClientOnly>
            <InlineEdit
              isEditing={editingSection?.type === 'sender'}
              onEditClick={() => setEditingSection({ type: 'sender' })}
              onSave={() => setEditingSection(null)}
              isPlaceholder={currentInvoice.sender.isPlaceholder}
              showDefaultSaveButton={false}
              displayContent={
                <div>
                  <h3 className="text-sm font-medium tracking-wide text-gray-500 uppercase mb-4">{t('invoice.from')}</h3>
                  <div className="space-y-3">
                    <div>
                      <p className={currentInvoice.sender.isPlaceholder?.company ? "text-gray-400 italic text-lg" : "text-xl font-medium text-gray-900"}>{currentInvoice.sender.company}</p>
                      <div className="mt-2 space-y-1 text-gray-600">
                        <p className={currentInvoice.sender.isPlaceholder?.address ? "text-gray-400 italic" : ""}>{currentInvoice.sender.address}</p>
                        <p className={currentInvoice.sender.isPlaceholder?.postalCode || currentInvoice.sender.isPlaceholder?.city ? "text-gray-400 italic" : ""}>
                          {currentInvoice.sender.postalCode} {currentInvoice.sender.city}
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-100">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">{t('invoice.vat')}</p>
                          <p className={currentInvoice.sender.isPlaceholder?.vatNumber ? "text-gray-400 italic" : "text-gray-600"}>
                            {currentInvoice.sender.vatNumber}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">{t('invoice.kvk')}</p>
                          <p className={currentInvoice.sender.isPlaceholder?.kvkNumber ? "text-gray-400 italic" : "text-gray-600"}>
                            {currentInvoice.sender.kvkNumber}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              }
              editContent={
                <div className="space-y-4">
                  <FloatingLabelInput
                    name="company"
                    type="text"
                    value={getInputValue('sender', 'company')}
                    placeholder={getPlaceholder(currentInvoice.sender.company, focusedFields.sender.company || false)}
                    onChange={(e) => updateSender('company', e.target.value)}
                    onCustomFocus={() => handleFocus('sender', 'company')}
                    onCustomBlur={() => handleBlur('sender', 'company')}
                    label={t('invoice.company')}
                    isPlaceholder={currentInvoice.sender.isPlaceholder?.company}
                  />
                  <FloatingLabelInput
                    name="address"
                    type="text"
                    value={getInputValue('sender', 'address')}
                    placeholder={getPlaceholder(currentInvoice.sender.address, focusedFields.sender.address || false)}
                    onChange={(e) => updateSender('address', e.target.value)}
                    onCustomFocus={() => handleFocus('sender', 'address')}
                    onCustomBlur={() => handleBlur('sender', 'address')}
                    label={t('invoice.address')}
                    isPlaceholder={currentInvoice.sender.isPlaceholder?.address}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <FloatingLabelInput
                      name="postalCode"
                      type="text"
                      value={getInputValue('sender', 'postalCode')}
                      placeholder={getPlaceholder(currentInvoice.sender.postalCode, focusedFields.sender.postalCode || false)}
                      onChange={(e) => updateSender('postalCode', e.target.value)}
                      onCustomFocus={() => handleFocus('sender', 'postalCode')}
                      onCustomBlur={() => handleBlur('sender', 'postalCode')}
                      label={t('invoice.postalCode')}
                      isPlaceholder={currentInvoice.sender.isPlaceholder?.postalCode}
                    />
                    <FloatingLabelInput
                      name="city"
                      type="text"
                      value={getInputValue('sender', 'city')}
                      placeholder={getPlaceholder(currentInvoice.sender.city, focusedFields.sender.city || false)}
                      onChange={(e) => updateSender('city', e.target.value)}
                      onCustomFocus={() => handleFocus('sender', 'city')}
                      onCustomBlur={() => handleBlur('sender', 'city')}
                      label={t('invoice.city')}
                      isPlaceholder={currentInvoice.sender.isPlaceholder?.city}
                    />
                  </div>
                  <FloatingLabelInput
                    name="vatNumber"
                    type="text"
                    value={getInputValue('sender', 'vatNumber')}
                    placeholder={getPlaceholder(currentInvoice.sender.vatNumber, focusedFields.sender.vatNumber || false)}
                    onChange={(e) => updateSender('vatNumber', e.target.value)}
                    onCustomFocus={() => handleFocus('sender', 'vatNumber')}
                    onCustomBlur={() => handleBlur('sender', 'vatNumber')}
                    label={t('invoice.vatNumber')}
                    isPlaceholder={currentInvoice.sender.isPlaceholder?.vatNumber}
                  />
                  <FloatingLabelInput
                    name="kvkNumber"
                    type="text"
                    value={getInputValue('sender', 'kvkNumber')}
                    placeholder={getPlaceholder(currentInvoice.sender.kvkNumber, focusedFields.sender.kvkNumber || false)}
                    onChange={(e) => updateSender('kvkNumber', e.target.value)}
                    onCustomFocus={() => handleFocus('sender', 'kvkNumber')}
                    onCustomBlur={() => handleBlur('sender', 'kvkNumber')}
                    label={t('invoice.kvkNumber')}
                    isPlaceholder={currentInvoice.sender.isPlaceholder?.kvkNumber}
                  />
                  <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    {isCurrentCompanySaved ? (
                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {t('company.yourCompany')}
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          saveAsDefaultCompany();
                          setEditingSection(null);
                        }}
                        className="btn-subtle btn-sm"
                      >
                        {t('company.saveAsDefault')}
                      </button>
                    )}
                    <button
                      onClick={() => setEditingSection(null)}
                      className="btn-primary btn-sm"
                    >
                      {t('actions.save')}
                    </button>
                  </div>
                </div>
              }
            />
          </ClientOnly>
        </div>

        {/* Line Items */}
        <div className="mb-8">
          <InvoiceLineItems />
        </div>

        {/* Notes and Payment Method */}
        <div className="border-t pt-4">
          {/* Payment Method */}
          <PaymentMethodEdit
            isEditing={editingSection?.type === 'paymentMethods'}
            onEditClick={() => setEditingSection({ type: 'paymentMethods' })}
            onSave={() => setEditingSection(null)}
          />

          {/* Footer Notes */}
          <div className="mt-6">
            <InlineEdit
              isEditing={editingSection?.type === 'notes'}
              onEditClick={() => setEditingSection({ type: 'notes' })}
              onSave={() => {
                const saveAsDefault = document.getElementById('save-notes-default') as HTMLInputElement;
                if (saveAsDefault && saveAsDefault.checked) {
                  updateNotes(currentInvoice.notes, true);
                }
                setEditingSection(null);
              }}
              isPlaceholder={{ notes: currentInvoice.isPlaceholder?.notes || false }}
              displayContent={
                <div className={`invoice-notes ${currentInvoice.isPlaceholder?.notes ? "text-gray-400 italic" : "text-gray-600"}`}>
                  <p>{currentInvoice.notes}</p>
                </div>
              }
              editContent={
                <div className="footer-notes">
                  <label className="block text-sm font-medium text-gray-700">{t('invoice.notes')}</label>
                  <textarea
                    value={currentInvoice.isPlaceholder?.notes ? '' : currentInvoice.notes}
                    placeholder={getPlaceholder(currentInvoice.notes, focusedFields.notes)}
                    onChange={(e) => updateNotes(e.target.value)}
                    onFocus={() => handleFocus('notes')}
                    onBlur={() => handleBlur('notes')}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                  <div className="mt-2 flex items-center">
                    <input
                      id="save-notes-default"
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      defaultChecked={currentInvoice.notes === settings.defaultNotes}
                    />
                    <label htmlFor="save-notes-default" className="ml-2 block text-sm text-gray-700">
                      {t('invoice.saveAsDefault')}
                    </label>
                  </div>
                </div>
              }
            />
          </div>
        </div>
        
        {/* Non-editable overlay */}
        <InvoiceNonEditableOverlay 
          status={currentInvoice.status}
          paymentStatus={currentInvoice.paymentStatus}
        />
      </div>
    );
  };

  return (
    <>
      <Head
        title="InvoiceHappens - Create Beautiful Invoices"
        metas={[
          {
            name: 'description',
            content: 'Create beautiful, professional invoices with InvoiceHappens. Easy to use, powerful features, and compliant with Dutch regulations.'
          },
          {
            property: 'og:title',
            content: 'InvoiceHappens - Create Beautiful Invoices'
          },
          {
            property: 'og:description',
            content: 'Create beautiful, professional invoices with InvoiceHappens. Easy to use, powerful features, and compliant with Dutch regulations.'
          },
          {
            property: 'og:type',
            content: 'website'
          },
          {
            name: 'twitter:card',
            content: 'summary_large_image'
          }
        ]}
        links={[
          {
            rel: 'canonical',
            href: 'https://invoicehappens.com'
          }
        ]}
      />

      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
        {/* Hero with integrated invoice form */}
        <Hero
          invoiceControls={
            <>
              <div className="p-4">
                <NewInvoiceButton />
              </div>
              <InvoiceSettings />
            </>
          }
          afterContent={
            <>
              {/* Command Bar */}
              <div className="mt-8">
                <CommandBar />
              </div>

              {/* Login Button */}
              <div className="max-w-4xl mx-auto px-8 mb-8">
                <ClientOnlyLoginButton />
              </div>

              {/* Invoice Overview */}
              <InvoiceOverview />
            </>
          }
        >
          {renderInvoiceForm()}
        </Hero>

        {/* Easy Section */}
        <Easy />

        {/* Apple Cards Carousel Demo */}
        <AppleCardsCarouselDemo />

        {/* FAQ Section */}
        <SimpleFaqsWithBackground />

        {/* Footer */}
        <Footer />

        {/* Schedule Edit Modal */}
        <ScheduleEdit />
      </div>
    </>
  );
};

export default LandingPage;
